import { z } from "zod";
import { ERROR_CODES } from "@/utils/error-transformer";
import { apiService } from "../api.service";
import { type ServiceResult, ServiceResultSchema } from "../job/job.types";
import {
	FormDefinitionApiError,
	type FormDefinitionWithScope,
	parseFormDefinitionsPageResponse,
} from "./form-definition.types";
import {
	type CreateFormDefinitionInfo,
	type EditFormDefinitionInfo,
	type EditFormLayoutInfo,
	type FormDefinition,
	FormDefinitionSchema,
	type FormDefinitionScopeQuery,
	type FormDefinitionsListParams,
	type ScopeFields,
} from "./form-definition-core.types";

/**
 * Flattens list query params for GET /form-definitions/count and paginated list.
 */
export function toFormDefinitionListParams(
	params?: FormDefinitionsListParams,
):
	| Record<string, string | number | boolean | string[] | undefined>
	| undefined {
	if (!params) return undefined;
	const flat: Record<string, string | number | boolean | string[] | undefined> =
		{};
	for (const [k, v] of Object.entries(params)) {
		if (v !== undefined) flat[k] = v;
	}
	return Object.keys(flat).length ? flat : undefined;
}

/**
 * Query object for GET /form-definitions/current and /active.
 */
export function toFormDefinitionScopeParams(
	params?: FormDefinitionScopeQuery,
):
	| Record<string, string | number | boolean | string[] | undefined>
	| undefined {
	if (!params) return undefined;
	const flat: Record<string, string | number | boolean | string[] | undefined> =
		{};
	for (const [k, v] of Object.entries(params)) {
		if (v !== undefined) flat[k] = v;
	}
	return Object.keys(flat).length ? flat : undefined;
}

function zodErrorMessage(error: z.ZodError): string {
	return error.issues
		.map(
			(iss) =>
				`${iss.path.length ? iss.path.join(".") : "(root)"}: ${iss.message}`,
		)
		.join(", ");
}

/** Optional request scope for platform admins acting in an org-admin URL context. */
export interface FormDefinitionRequestOptions {
	organizationId?: string;
}

function organizationIdHeaders(
	options?: FormDefinitionRequestOptions,
): Record<string, string> | undefined {
	const id = options?.organizationId;
	if (id && id.length > 0) {
		return { "organization-id": id };
	}
	return undefined;
}

export class FormDefinitionService {
	/**
	 * GET /form-definitions/count
	 */
	async getFormDefinitionsCount(
		params?: FormDefinitionsListParams,
	): Promise<number> {
		try {
			const response = await apiService.get<number>("/form-definitions/count", {
				params: toFormDefinitionListParams(params),
			});
			return z.number().parse(response);
		} catch (error) {
			if (error instanceof FormDefinitionApiError) throw error;
			if (error instanceof z.ZodError) {
				throw new FormDefinitionApiError(
					`Form definitions count response validation failed: ${zodErrorMessage(error)}`,
					error,
				);
			}
			throw new FormDefinitionApiError(
				`Failed to get form definitions count: ${error instanceof Error ? error.message : "Unknown error"}`,
				error,
			);
		}
	}

	/**
	 * GET /form-definitions/{start}/{count}
	 */
	async getFormDefinitionsPage(
		start: number,
		count: number,
		params?: FormDefinitionsListParams,
	): Promise<FormDefinitionWithScope[]> {
		try {
			const response = await apiService.get<unknown>(
				`/form-definitions/${start}/${count}`,
				{ params: toFormDefinitionListParams(params) },
			);
			return parseFormDefinitionsPageResponse(response);
		} catch (error) {
			if (error instanceof FormDefinitionApiError) throw error;
			throw new FormDefinitionApiError(
				`Failed to get form definitions page: ${error instanceof Error ? error.message : "Unknown error"}`,
				error,
			);
		}
	}

	/**
	 * GET /form-definitions/{id}
	 */
	async getFormDefinitionById(id: string): Promise<FormDefinition> {
		try {
			const response = await apiService.get<unknown>(
				`/form-definitions/${encodeURIComponent(id)}`,
			);
			return FormDefinitionSchema.parse(response);
		} catch (error) {
			if (error instanceof FormDefinitionApiError) throw error;
			if (error instanceof z.ZodError) {
				throw new FormDefinitionApiError(
					`Form definition ${id} response validation failed: ${zodErrorMessage(error)}`,
					error,
				);
			}
			throw new FormDefinitionApiError(
				`Failed to get form definition ${id}: ${error instanceof Error ? error.message : "Unknown error"}`,
				error,
			);
		}
	}

	/**
	 * GET /form-definitions/current
	 *
	 * @returns `null` when the API responds with 404 (no form definition for the given scope yet).
	 */
	async getCurrentFormDefinition(
		params?: FormDefinitionScopeQuery,
		requestOptions?: FormDefinitionRequestOptions,
	): Promise<FormDefinition | null> {
		try {
			const response = await apiService.get<unknown>(
				"/form-definitions/current",
				{
					params: toFormDefinitionScopeParams(params),
					headers: organizationIdHeaders(requestOptions),
				},
			);
			return FormDefinitionSchema.parse(response);
		} catch (error) {
			if (
				error !== null &&
				typeof error === "object" &&
				(("status" in error && (error as { status?: number }).status === 404) ||
					("code" in error &&
						(error as { code?: string }).code === ERROR_CODES.NOT_FOUND))
			) {
				return null;
			}
			if (error instanceof FormDefinitionApiError) throw error;
			if (error instanceof z.ZodError) {
				throw new FormDefinitionApiError(
					`Current form definition response validation failed: ${zodErrorMessage(error)}`,
					error,
				);
			}
			throw new FormDefinitionApiError(
				`Failed to get current form definition: ${error instanceof Error ? error.message : "Unknown error"}`,
				error,
			);
		}
	}

	/**
	 * GET /form-definitions/active
	 */
	async getActiveFormDefinition(
		params?: FormDefinitionScopeQuery,
	): Promise<FormDefinition> {
		try {
			const response = await apiService.get<unknown>(
				"/form-definitions/active",
				{ params: toFormDefinitionScopeParams(params) },
			);
			return FormDefinitionSchema.parse(response);
		} catch (error) {
			if (error instanceof FormDefinitionApiError) throw error;
			if (error instanceof z.ZodError) {
				throw new FormDefinitionApiError(
					`Active form definition response validation failed: ${zodErrorMessage(error)}`,
					error,
				);
			}
			throw new FormDefinitionApiError(
				`Failed to get active form definition: ${error instanceof Error ? error.message : "Unknown error"}`,
				error,
			);
		}
	}

	/**
	 * POST /form-definitions
	 */
	async createFormDefinition(
		body: CreateFormDefinitionInfo,
		requestOptions?: FormDefinitionRequestOptions,
	): Promise<ServiceResult> {
		try {
			const response = await apiService.post<unknown>(
				"/form-definitions",
				body,
				{ headers: organizationIdHeaders(requestOptions) },
			);
			const result = ServiceResultSchema.parse(response);
			if (!result.success) {
				throw FormDefinitionApiError.fromServiceResult(result);
			}
			return result;
		} catch (error) {
			if (error instanceof FormDefinitionApiError) throw error;
			if (error instanceof z.ZodError) {
				throw new FormDefinitionApiError(
					`Create form definition response validation failed: ${zodErrorMessage(error)}`,
					error,
				);
			}
			throw new FormDefinitionApiError(
				`Failed to create form definition: ${error instanceof Error ? error.message : "Unknown error"}`,
				error,
			);
		}
	}

	/**
	 * PUT /form-definitions/{id}
	 */
	async updateFormDefinition(
		id: string,
		body: EditFormDefinitionInfo,
		requestOptions?: FormDefinitionRequestOptions,
	): Promise<ServiceResult> {
		try {
			const response = await apiService.put<unknown>(
				`/form-definitions/${encodeURIComponent(id)}`,
				body,
				{ headers: organizationIdHeaders(requestOptions) },
			);
			const result = ServiceResultSchema.parse(response);
			if (!result.success) {
				throw FormDefinitionApiError.fromServiceResult(result);
			}
			return result;
		} catch (error) {
			if (error instanceof FormDefinitionApiError) throw error;
			if (error instanceof z.ZodError) {
				throw new FormDefinitionApiError(
					`Update form definition ${id} response validation failed: ${zodErrorMessage(error)}`,
					error,
				);
			}
			throw new FormDefinitionApiError(
				`Failed to update form definition ${id}: ${error instanceof Error ? error.message : "Unknown error"}`,
				error,
			);
		}
	}

	/**
	 * POST /form-definitions/{id}/version
	 */
	async createFormDefinitionVersion(
		id: string,
		body: ScopeFields,
	): Promise<ServiceResult> {
		try {
			const response = await apiService.post<unknown>(
				`/form-definitions/${encodeURIComponent(id)}/version`,
				body,
			);
			const result = ServiceResultSchema.parse(response);
			if (!result.success) {
				throw FormDefinitionApiError.fromServiceResult(result);
			}
			return result;
		} catch (error) {
			if (error instanceof FormDefinitionApiError) throw error;
			if (error instanceof z.ZodError) {
				throw new FormDefinitionApiError(
					`Create form definition version ${id} response validation failed: ${zodErrorMessage(error)}`,
					error,
				);
			}
			throw new FormDefinitionApiError(
				`Failed to create form definition version for ${id}: ${error instanceof Error ? error.message : "Unknown error"}`,
				error,
			);
		}
	}

	/**
	 * PUT /form-definitions/{id}/layout
	 */
	async updateFormDefinitionLayout(
		id: string,
		body: EditFormLayoutInfo,
	): Promise<ServiceResult> {
		try {
			const response = await apiService.put<unknown>(
				`/form-definitions/${encodeURIComponent(id)}/layout`,
				body,
			);
			const result = ServiceResultSchema.parse(response);
			if (!result.success) {
				throw FormDefinitionApiError.fromServiceResult(result);
			}
			return result;
		} catch (error) {
			if (error instanceof FormDefinitionApiError) throw error;
			if (error instanceof z.ZodError) {
				throw new FormDefinitionApiError(
					`Update form definition layout ${id} response validation failed: ${zodErrorMessage(error)}`,
					error,
				);
			}
			throw new FormDefinitionApiError(
				`Failed to update form definition layout ${id}: ${error instanceof Error ? error.message : "Unknown error"}`,
				error,
			);
		}
	}

	/**
	 * PUT /form-definitions/{id}/activate
	 */
	async activateFormDefinition(
		id: string,
		requestOptions?: FormDefinitionRequestOptions,
	): Promise<ServiceResult> {
		try {
			const response = await apiService.put<unknown>(
				`/form-definitions/${encodeURIComponent(id)}/activate`,
				undefined,
				{ headers: organizationIdHeaders(requestOptions) },
			);
			const result = ServiceResultSchema.parse(response);
			if (!result.success) {
				throw FormDefinitionApiError.fromServiceResult(result);
			}
			return result;
		} catch (error) {
			if (error instanceof FormDefinitionApiError) throw error;
			if (error instanceof z.ZodError) {
				throw new FormDefinitionApiError(
					`Activate form definition ${id} response validation failed: ${zodErrorMessage(error)}`,
					error,
				);
			}
			throw new FormDefinitionApiError(
				`Failed to activate form definition ${id}: ${error instanceof Error ? error.message : "Unknown error"}`,
				error,
			);
		}
	}
}

export const formDefinitionService = new FormDefinitionService();
